import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import entityComposition from '../common/classes/entityComposition';
import FormEditor from './formEditor';
import Loader from '../common/loader';
import { formTypePermissionSelector } from '../common/classes/permissions';
import appHistory from '../../utils/appHistory';
import { loadDirectFormSession, normalizeDirectFormId, saveDirectFormSession } from './directFormSession';
const PERSIST_DEBOUNCE_MS = 300;
const DirectFormContainer = _ref => {
  let _ref$form = _ref.form,
    _ref$form2 = _ref$form === void 0 ? {
      active: {
        isNew: false
      }
    } : _ref$form,
    active = _ref$form2.active,
    _ref$form2$active = _ref$form2.active,
    _ref$form2$active2 = _ref$form2$active === void 0 ? {} : _ref$form2$active,
    _ref$form2$active2$co = _ref$form2$active2.content,
    _ref$form2$active2$co2 = _ref$form2$active2$co === void 0 ? {} : _ref$form2$active2$co,
    title = _ref$form2$active2$co2.title,
    activeId = _ref$form2$active2.id,
    _ref$form2$pendingCha = _ref$form2.pendingChanges,
    pendingChanges = _ref$form2$pendingCha === void 0 ? false : _ref$form2$pendingCha,
    _ref$formActions = _ref.formActions,
    formActions = _ref$formActions === void 0 ? {} : _ref$formActions,
    id = _ref.match.params.id;
  const routeIdNorm = normalizeDirectFormId(id);
  const activeIdNorm = normalizeDirectFormId(activeId);
  const hasStaleForm = Boolean(routeIdNorm && activeIdNorm && routeIdNorm !== activeIdNorm);
  // Track which route id has been cold-load handled (instead of a permanent
  // boolean latch) so restore/redirect re-runs when the route :id changes
  // while the component stays mounted (in-app nav, browser back/forward).
  const coldLoadHandledIdRef = useRef(null);
  const _useState = useState(() => Boolean(routeIdNorm && !activeIdNorm)),
    _useState2 = _slicedToArray(_useState, 2),
    awaitingRestore = _useState2[0],
    setAwaitingRestore = _useState2[1];

  // Re-arm the loader state when the route or active id changes while mounted.
  useEffect(() => {
    setAwaitingRestore(Boolean(routeIdNorm && !activeIdNorm));
  }, [routeIdNorm, activeIdNorm]);

  // Another screen (e.g. cabinet form) may still own form.active — e.g. after
  // browser back to /directForms/:id. Clear so we do not render the wrong form.
  useEffect(() => {
    if (hasStaleForm && formActions !== null && formActions !== void 0 && formActions.showCancel) {
      formActions.showCancel();
    }
  }, [hasStaleForm, formActions]);

  // On cold load (refresh), restore from sessionStorage or fall back to a new form.
  useLayoutEffect(() => {
    if (coldLoadHandledIdRef.current === routeIdNorm || activeIdNorm || hasStaleForm || !routeIdNorm) {
      return;
    }
    coldLoadHandledIdRef.current = routeIdNorm;
    const snapshot = loadDirectFormSession(routeIdNorm);
    if (snapshot && formActions !== null && formActions !== void 0 && formActions.restoreDirectFormSession) {
      formActions.restoreDirectFormSession(snapshot);
      setAwaitingRestore(false);
      return;
    }
    if (snapshot !== null && snapshot !== void 0 && snapshot.formTypeIdentifier) {
      appHistory.replace("/directForms/new/".concat(snapshot.formTypeIdentifier));
      return;
    }
    setAwaitingRestore(false);
    appHistory.replace('/');
  }, [activeIdNorm, formActions, hasStaleForm, routeIdNorm]);

  // Debounce persistence so large-form edits don't serialize + write to
  // sessionStorage synchronously on every change.
  const persistRef = useRef(null);
  if (!persistRef.current) {
    persistRef.current = debounce((formId, snapshot) => {
      saveDirectFormSession(formId, snapshot);
    }, PERSIST_DEBOUNCE_MS);
  }

  // Keep sessionStorage in sync while editing a direct form.
  useEffect(() => {
    if (!routeIdNorm || !activeIdNorm || routeIdNorm !== activeIdNorm || !(active !== null && active !== void 0 && active.id)) {
      return undefined;
    }
    persistRef.current(routeIdNorm, {
      active,
      pendingChanges
    });
    return undefined;
  }, [active, activeIdNorm, pendingChanges, routeIdNorm]);

  // Flush pending writes on unmount and on browser unload so a refresh right
  // after an edit still persists the latest snapshot.
  useEffect(() => {
    const persist = persistRef.current;
    const flush = () => persist.flush();
    global.window.addEventListener('beforeunload', flush);
    return () => {
      global.window.removeEventListener('beforeunload', flush);
      persist.flush();
    };
  }, []);

  // Clean up Redux form state when leaving a direct form so the next page
  // (e.g. a regular form editor) starts with a clean slate.
  useEffect(() => () => {
    if (formActions !== null && formActions !== void 0 && formActions.showCancel) {
      formActions.showCancel();
    }
  }, [formActions]);
  if (!activeId || hasStaleForm || awaitingRestore) {
    return /*#__PURE__*/React.createElement(Loader, {
      style: {
        marginTop: '50%'
      },
      loaded: false
    });
  }
  return /*#__PURE__*/React.createElement(FormEditor, {
    id: id,
    formTitle: title,
    sectionsLabel: "Sections",
    showSave: false,
    hideBackButton: true,
    related: {
      suppressUnsavedChangesNotification: true
    }
  });
};
DirectFormContainer.propTypes = {
  form: PropTypes.shape({
    active: PropTypes.shape({
      content: PropTypes.shape({
        title: PropTypes.string
      }),
      id: PropTypes.string,
      isNew: PropTypes.bool
    }),
    loading: PropTypes.bool.isRequired,
    pendingChanges: PropTypes.bool.isRequired
  }),
  formActions: PropTypes.shape({
    restoreDirectFormSession: PropTypes.func,
    showCancel: PropTypes.func
  }),
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string
    }),
    url: PropTypes.string
  }).isRequired
};
export { DirectFormContainer as TestDirectFormContainer };
export default entityComposition(DirectFormContainer, ['form'], {
  canView: formTypePermissionSelector('view_detail')
}, []);